import React, { useEffect, useRef, useState, createRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Bar } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import "../../styles/Dashboard.css";
import { Button, Form, Popup, Grid, Loader } from "semantic-ui-react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";
import "../../uiComponents/Dropdown/DropdownComponent.css";
import {
  fetchEngagement,
  fetchEngagementSuccess,
} from "../../actions/dataConsoleAction";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { format, set } from "date-fns";
import DashLoader from "../../components/DashLoader";
import DropLoader from "../../components/DropLoader";
import { minWidth } from "@mui/system";
import { Height } from "@mui/icons-material";
import { getLocalTimeZoneAbbr } from '../../utils/formatMessageTimestamp';
import DownloadIcon from "../../components/DownloadIcon";
import { handleExport } from "../../utils/exportUtils";

// Register the plugins for Chart.js
Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const EngagementDashboard = ({
  selectedDatasetId,
  selectedDatasetName,
  globalStartDate,
  globalEndDate,
  globalSelectedGroup,
  globalSelectedUsers, // Array of selected user IDs
  globalUsers,
  globalEngagementData,
  globalEngagementLoading,
  datasets, // Dataset list from parent
  globalGroups, // Groups list from parent
}) => {
  const datasetId = selectedDatasetId.selectedDatasetId || "";

  // Get timezone abbreviation for display on time-based charts
  const tzAbbrev = getLocalTimeZoneAbbr(new Date());

  // Helper function to get selected users display text
  const getSelectedUsersText = () => {
    if (!globalSelectedUsers || globalSelectedUsers.length === 0) return 'None';
    if (globalSelectedUsers.length === globalUsers.length) return 'All Users';
    if (globalSelectedUsers.length === 1) {
      const user = globalUsers.find(u => u.userId === globalSelectedUsers[0]);
      return user ? user.username : globalSelectedUsers[0];
    }
    return `${globalSelectedUsers.length} Users`;
  };

  // Helper function to get group name from ID
  const getGroupName = () => {
    if (!globalSelectedGroup) return 'All Groups';
    const group = globalGroups.find(g => g.id === globalSelectedGroup);
    return group ? group.name : 'All Groups';
  };

  // Updated conversion functions to handle the new consistent format
  const convertTimeToMinutes = (timeString) => {
    if (!timeString) return 0;

    const timeParts = timeString.split(" ");
    let totalMinutes = 0;

    for (let i = 0; i < timeParts.length; i += 2) {
      const value = parseInt(timeParts[i], 10);
      const unit = timeParts[i + 1];

      if (unit && unit.startsWith("hr")) {
        totalMinutes += value * 60;
      } else if (unit && unit.startsWith("min")) {
        totalMinutes += value;
      } else if (unit && unit.startsWith("sec")) {
        totalMinutes += value / 60;
      }
    }

    return Math.max(totalMinutes, 0.1);
  };

  const convertTimeToSeconds = (timeString) => {
    if (!timeString) return 0;

    const timeParts = timeString.split(" ");
    let totalSeconds = 0;

    for (let i = 0; i < timeParts.length; i += 2) {
      const value = parseInt(timeParts[i], 10);
      const unit = timeParts[i + 1];

      if (unit && unit.startsWith("hr")) {
        totalSeconds += value * 3600;
      } else if (unit && unit.startsWith("min")) {
        totalSeconds += value * 60;
      } else if (unit && unit.startsWith("sec")) {
        totalSeconds += value;
      }
    }

    return totalSeconds;
  };

  // Convert a 24-hour value into a 12-hour clock display, e.g.
  //   0  -> "12:00 AM"
  //   13 -> "1:00 PM"
  //   23 -> "11:00 PM"
  // Accepts a number, a numeric string, or a string like "14:00".
  const formatHour12 = (input) => {
    if (input === null || input === undefined || input === "") return "";
    const raw = typeof input === "string" ? input : String(input);
    const match = raw.match(/^(\d+)/);
    if (!match) return raw;
    const n = ((parseInt(match[1], 10) % 24) + 24) % 24;
    const period = n < 12 ? "AM" : "PM";
    const display = n % 12 === 0 ? 12 : n % 12;
    return `${display}:00 ${period}`;
  };

  // Parse peak hour strings from the API (e.g. "3:00 PM" or "15:00") to 0-23.
  const parsePeakHourTo24 = (input) => {
    if (input === null || input === undefined || input === "") return null;
    const raw = String(input).trim();
    const meridiemMatch = raw.match(/^(\d{1,2})(?::\d{2})?\s*(AM|PM)$/i);
    if (meridiemMatch) {
      let hour = parseInt(meridiemMatch[1], 10);
      const period = meridiemMatch[2].toUpperCase();
      if (period === "PM" && hour < 12) hour += 12;
      if (period === "AM" && hour === 12) hour = 0;
      return hour;
    }
    const match = raw.match(/^(\d{1,2})/);
    if (!match) return null;
    return ((parseInt(match[1], 10) % 24) + 24) % 24;
  };

  // Normalize API peak hour strings to a consistent 12-hour label.
  // Handles both legacy ("20:00 PM") and current ("3:00 PM") backend formats.
  const formatPeakHourDisplay = (input) => {
    if (!input || input === "N/A") return input || "";
    const hour24 = parsePeakHourTo24(input);
    return hour24 !== null ? formatHour12(hour24) : input;
  };

  const convertMinutesToHours = (timeString) => {
    const totalSeconds = convertTimeToSeconds(timeString);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const formattedTime = [
      hours > 0 ? `${hours} hr` : "",
      minutes > 0 ? `${minutes} min` : "",
      seconds > 0 ? `${seconds} sec` : "",
    ]
      .filter(Boolean)
      .join(" ");

    return formattedTime || "0 sec";
  };

  // Parse "YYYY-MM-DD" as local date (not UTC)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return new Date(NaN);
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Utility function to generate date range array
  const generateDateRange = (start, end) => {
    if (!start || !end) return [];
    const dateArray = [];
    let currentDate = new Date(start);
    while (currentDate <= end) {
      dateArray.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dateArray;
  };

  // Average Engagement Time Component
  const renderAverageEngagementTime = () => {
    const chartRef = useRef(null);
    const dateRange = generateDateRange(globalStartDate, globalEndDate);
    const [weeklyData, setWeeklyData] = useState(
      Array(dateRange.length).fill(0)
    );
    const [chatTime, setChatTime] = useState("0 min 0 sec");

    const processEngagementData = (data) => {
      // Backend already filters by selected user, so use engagementByDay directly
      const engagementByDay = data.engagementByDay || [];
      const dailyEngagement = Array(dateRange.length).fill(0);

      engagementByDay.forEach((record) => {
        const recordDate = parseLocalDate(record.date);
        const normalizedRecordDate = new Date(recordDate.setHours(0, 0, 0, 0));

        dateRange.forEach((date, index) => {
          const normalizedDate = new Date(date.getTime()).setHours(0, 0, 0, 0);
          if (normalizedRecordDate.getTime() === normalizedDate) {
            const engagementTimeInMinutes = convertTimeToMinutes(
              record.engagementTime
            );
            dailyEngagement[index] += engagementTimeInMinutes;
          }
        });
      });
      setWeeklyData(dailyEngagement);
    };

    useEffect(() => {
      // Use global engagement data instead of fetching
      if (!globalStartDate || !globalEndDate || !globalEngagementData) {
        setWeeklyData(Array(dateRange.length).fill(0));
        setChatTime("0 min 0 sec");
        return;
      }

      // Backend now returns a simple string value for the selected date range
      const averageSessionDurationForRange = globalEngagementData.averageSessionDurationForRange;

      if (averageSessionDurationForRange) {
        setChatTime(averageSessionDurationForRange);
      } else {
        setChatTime("0 min 0 sec");
      }

      processEngagementData(globalEngagementData);
    }, [globalEngagementData, globalStartDate, globalEndDate]);

    const handleAverageEngagementExport = async (format, data, filename, chartCanvas) => {
      const exportData = {
        labels: dateRange.map((date) =>
          date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
          })
        ),
        values: weeklyData
      };

      const metadata = {
        title: 'Average Engagement Time',
        subtitle: `Avg Chat Time - ${chatTime}`,
        selectedDataset: selectedDatasetName || 'All Datasets',
        selectedGroup: getGroupName(),
        selectedUsers: getSelectedUsersText(),
        dateRange: `${formatDate(globalStartDate)} - ${formatDate(globalEndDate)}`,
        labelHeader: 'Day',
        valueHeader: 'Time (min)'
      };

      await handleExport(format, exportData, `Average_Engagement_Time_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`, metadata, chartCanvas);
    };

    const chartData = {
      labels: dateRange.map((date) =>
        date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      ),
      datasets: [
        {
          data: weeklyData,
          borderColor: "#0053ED",
          backgroundColor: "#0053ED",
          borderWidth: 1,
          barPercentage: 0.3,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            callback: function (value) {
              return value;
            },
          },
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: " Time (min)",
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        x: {
          title: {
            display: true,
            text: "Day",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const index = context.dataIndex;
              const date = formatDate(dateRange[index]);
              const weeklyDataValue = weeklyData[index]
                ? Math.round(weeklyData[index] * 100) / 100
                : 0;
              return `${weeklyDataValue}min on ${date}`;
            },
          },
        },
        datalabels: {
          display: false,
        },
      },
    };

    return (
      <Grid
        className="card_dashboards"
        style={{
          minHeight: "430px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {globalEngagementLoading ? (
          <DashLoader />
        ) : (
          <>
            <Grid.Row>
              <Grid.Column className="card-header-title">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <div>
                    <Popup
                      content="The average duration user(s) spend actively interacting with the bot"
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <label className="chart-title">
                          Average Engagement Time
                        </label>
                      }
                    />
                    <h4 className="card-subtitles">Avg Chat Time - {chatTime}</h4>
                  </div>
                  <DownloadIcon
                    onExport={handleAverageEngagementExport}
                    filename={`Average_Engagement_Time_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`}
                    chartRef={chartRef}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16} className="chart-containers">
                <Bar ref={chartRef} data={chartData} options={options} />
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>
    );
  };

  // Engagement Time per User Component - Using global selections
  const renderEngagementTimePerUser = () => {
    const chartRef = useRef(null);
    const [userData, setUserData] = useState([]);
    const [chatTime, setChatTime] = useState("0 min 0 sec");

    // Helper function to format time consistently (moved from backend logic)
    const formatTimeConsistently = (timeInSeconds) => {
      if (!timeInSeconds || timeInSeconds < 0) {
        return "0 min 0 sec";
      }

      const hours = Math.floor(timeInSeconds / 3600);
      const minutes = Math.floor((timeInSeconds % 3600) / 60);
      const seconds = Math.floor(timeInSeconds % 60);

      let formattedTime = "";

      if (hours > 0) {
        formattedTime += `${hours} hr `;
      }

      formattedTime += `${minutes} min `;
      formattedTime += `${seconds} sec`;

      return formattedTime.trim();
    };

    // Process engagement data from global data
    useEffect(() => {
      if (!globalEngagementData) {
        setUserData([]);
        setChatTime("0 min 0 sec");
        return;
      }

      const dateWiseTotalEngagementTime =
        globalEngagementData.totalEngagementTime?.dateWiseTotalEngagementTime || [];

      const currentDateRange = generateDateRange(globalStartDate, globalEndDate);

      // Initialize users with 0 engagement time
      const aggregatedUserData = {};

      if (globalSelectedUsers && globalSelectedUsers.length > 0) {
        // If users are selected, show only selected users
        globalUsers.forEach((user) => {
          if (globalSelectedUsers.includes(user.userId)) {
            aggregatedUserData[user.userId] = {
              userId: user.userId,
              username: user.username,
              totalEngagementTimeInSeconds: 0,
            };
          }
        });
      }

      // Process data for the selected date range and add engagement time
      currentDateRange.forEach((date) => {
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        const selectedDateEngagement = dateWiseTotalEngagementTime.find(
          (record) => record.date === formattedDate
        );

        if (selectedDateEngagement && selectedDateEngagement.users) {
          selectedDateEngagement.users.forEach((user) => {
            // If no users selected, add all users from engagement data
            if (!aggregatedUserData[user.userId] && (!globalSelectedUsers || globalSelectedUsers.length === 0)) {
              // Find username from globalUsers if available
              const userInfo = globalUsers.find(u => u.userId === user.userId);
              aggregatedUserData[user.userId] = {
                userId: user.userId,
                username: userInfo?.username || user.username || user.userId,
                totalEngagementTimeInSeconds: 0,
              };
            }

            if (aggregatedUserData[user.userId]) {
              aggregatedUserData[user.userId].totalEngagementTimeInSeconds +=
                convertTimeToSeconds(user.totalEngagementTime);
            }
          });
        }
      });

      // Convert to array and sort by total engagement time
      const sortedUsers = Object.values(aggregatedUserData).sort(
        (a, b) => b.totalEngagementTimeInSeconds - a.totalEngagementTimeInSeconds
      );

      // Format user data
      const allUsersFormatted = sortedUsers.map((user) => {
        const engagementTimeInMinutes = convertTimeToMinutes(
          formatTimeConsistently(user.totalEngagementTimeInSeconds)
        );
        return {
          userId: user.userId,
          username: user.username,
          engagementTime:
            user.totalEngagementTimeInSeconds === 0
              ? 0.01
              : Math.max(engagementTimeInMinutes, 0.01),
          actualEngagementTime: engagementTimeInMinutes,
          totalEngagementTimeInSeconds: user.totalEngagementTimeInSeconds,
        };
      });

      setUserData(allUsersFormatted);

      // Backend now returns a simple string value for the selected date range
      const totalSessionDurationForRange = globalEngagementData.totalSessionDurationForRange;

      if (totalSessionDurationForRange) {
        setChatTime(totalSessionDurationForRange);
      } else {
        setChatTime("0 min 0 sec");
      }
    }, [globalEngagementData, globalUsers, globalStartDate, globalEndDate, globalSelectedUsers]);

    const handleEngagementPerUserExport = async (format, data, filename, chartCanvas) => {
      const exportData = {
        labels: userData.map((data) => data.username),
        values: userData.map((data) => Math.round(data.engagementTime * 100) / 100)
      };

      const metadata = {
        title: 'Engagement Time of Users',
        subtitle: `Total Chat Time - ${chatTime}`,
        selectedDataset: selectedDatasetName || 'All Datasets',
        selectedGroup: getGroupName(),
        selectedUsers: getSelectedUsersText(),
        dateRange: `${formatDate(globalStartDate)} - ${formatDate(globalEndDate)}`,
        labelHeader: 'User',
        valueHeader: 'Time (min)'
      };

      await handleExport(format, exportData, `Engagement_Time_Per_User_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`, metadata, chartCanvas);
    };

    const filteredUserData = userData;

    const dateRange = generateDateRange(globalStartDate, globalEndDate);
    const chartDatas = {
      labels: filteredUserData.map((data) => data.username),
      datasets: [
        {
          data: filteredUserData.map(
            (data) => Math.round(data.engagementTime * 100) / 100
          ),
          borderColor: "#0053ED",
          backgroundColor: "#0053ED",
          borderWidth: 1,
          barPercentage: 0.3,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          min: 0, // Ensure y-axis starts at 0
          ticks: {
            stepSize: 1, // Smaller step size to better show small values
            callback: function (value) {
              return value;
            },
          },
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: "Time (min)",
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        x: {
          title: {
            display: true,
            text: "User",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 45, // Rotate labels if needed
            minRotation: 0,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const index = context.dataIndex;
              const userData = filteredUserData[index];
              const actualEngagementTime =
                userData.actualEngagementTime || userData.engagementTime;
              const engagementSeconds = userData.totalEngagementTimeInSeconds;

              // Show actual engagement time, even if it's 0
              if (engagementSeconds === 0) {
                return `Engagement Time: 0 seconds`;
              } else if (actualEngagementTime < 1) {
                const seconds = Math.round(engagementSeconds);
                return `Engagement Time: ${seconds}s (${actualEngagementTime.toFixed(
                  2
                )}min)`;
              } else {
                const roundedTime =
                  Math.round(actualEngagementTime * 100) / 100;
                return `Engagement Time: ${roundedTime}min`;
              }
            },
          },
        },
        datalabels: {
          display: false, // Hide datalabels to avoid clutter with small values
        },
      },
    };

    return (
      <Grid
        className="card_dashboards"
        style={{
          minHeight: "430px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {globalEngagementLoading ? (
          <DashLoader />
        ) : (
          <>
            <Grid.Row>
              <Grid.Column className="card-header-title">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <div>
                    <Popup
                      content={`Engagement time of users for ${formatDate(
                        globalStartDate
                      )}${globalEndDate ? " - " + formatDate(globalEndDate) : ""}`}
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <label className="chart-title">
                          Engagement Time of Users
                        </label>
                      }
                    />
                    <h4 className="card-subtitles">Total Chat Time - {chatTime}</h4>
                  </div>
                  <DownloadIcon
                    onExport={handleEngagementPerUserExport}
                    filename={`Engagement_Time_Per_User_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`}
                    chartRef={chartRef}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16} className="chart-containers">
                <Bar ref={chartRef} data={chartDatas} options={options} />
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>
    );
  };

  // Peak Engagement Hours Component
  const renderPeakEngagementHours = () => {
    const chartRef = useRef(null);
    const dateRange = generateDateRange(globalStartDate, globalEndDate);
    const [hourlyData, setHourlyData] = useState(
      Array(dateRange.length)
        .fill()
        .map(() => ({ hour: null, engagementCount: 0 }))
    );
    const [chatTime, setChatTime] = useState("0 min 0 sec");

    useEffect(() => {
      if (!globalEngagementData || !globalStartDate || !globalEndDate) {
        setHourlyData(
          Array(dateRange.length).fill({ hour: null, engagementCount: 0 })
        );
        setChatTime("0 min 0 sec");
        return;
      }

      const peakHours = globalEngagementData.peakHours || [];
      const hourlyCounts = Array(dateRange.length)
        .fill(null)
        .map(() => ({ hour: null, engagementCount: 0 }));

      peakHours.forEach((record) => {
        const recordDate = parseLocalDate(record.date);
        const normalizedRecordDate = new Date(recordDate.setHours(0, 0, 0, 0));

        dateRange.forEach((date, index) => {
          const normalizedDate = new Date(date.getTime()).setHours(0, 0, 0, 0);
          if (normalizedRecordDate.getTime() === normalizedDate) {
            hourlyCounts[index] = {
              hour: record.hour,
              engagementCount: record.engagementCount,
            };
          }
        });
      });

      setHourlyData(hourlyCounts);

      // Backend now returns a simple string value for the selected date range
      const totalSessionDurationForRange = globalEngagementData.totalSessionDurationForRange;

      if (totalSessionDurationForRange) {
        setChatTime(totalSessionDurationForRange);
      } else {
        setChatTime("0 min 0 sec");
      }
    }, [globalEngagementData, globalStartDate, globalEndDate]);

    const handlePeakEngagementExport = async (format, data, filename, chartCanvas) => {
      // Create detailed data with peak hour time and engagement count
      const detailedData = dateRange.map((date, index) => {
        const dateStr = date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        });
        const hourData = hourlyData[index];
        const peakHour = hourData && hourData.hour ? hourData.hour : 'N/A';
        const engagementCount = hourData && hourData.engagementCount ? hourData.engagementCount : 0;

        // Format engagement count to readable time
        const formatEngagementTime = (seconds) => {
          if (!seconds || seconds === 0) return '0s';
          const hrs = Math.floor(seconds / 3600);
          const mins = Math.floor((seconds % 3600) / 60);
          const secs = Math.round(seconds % 60);
          let formattedTime = "";
          if (hrs > 0) formattedTime += `${hrs}h `;
          if (mins > 0) formattedTime += `${mins}m `;
          if (secs > 0) formattedTime += `${secs}s`;
          return formattedTime.trim() || "0s";
        };

        return {
          Day: dateStr,
          'Peak Hour': formatPeakHourDisplay(peakHour),
          'Engagement Duration': formatEngagementTime(engagementCount)
        };
      });

      const metadata = {
        title: 'Peak Engagement Hours',
        subtitle: `Total Chat Time - ${chatTime}`,
        selectedDataset: selectedDatasetName || 'All Datasets',
        selectedGroup: getGroupName(),
        selectedUsers: getSelectedUsersText(),
        dateRange: `${formatDate(globalStartDate)} - ${formatDate(globalEndDate)}`
      };

      await handleExport(format, detailedData, `Peak_Engagement_Hours_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`, metadata, chartCanvas);
    };

    function formatTime(seconds) {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = Math.round(seconds % 60);
      let formattedTime = "";
      if (hrs > 0) {
        formattedTime += `${hrs}h `;
      }
      if (mins > 0) {
        formattedTime += `${mins}m `;
      }
      if (secs > 0) {
        formattedTime += `${secs}s`;
      }
      return formattedTime.trim() || "0s";
    }

    const chartData = {
      labels: dateRange.map((date) =>
        date.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })
      ),
      datasets: [
        {
          label: "Peak Hour Engagement",
          data: hourlyData.map((data) => {
            const hour24 = parsePeakHourTo24(data.hour);
            return hour24 !== null ? hour24 : 0;
          }),
          borderColor: "#0053ED",
          backgroundColor: "#0053ED",
          borderWidth: 1,
          fill: false,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          max: 24,
          ticks: {
            stepSize: 2,
            callback: function (value) {
              return formatHour12(value);
            },
          },
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: `Time (${tzAbbrev})`,
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        x: {
          title: {
            display: true,
            text: "Day",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: true,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const index = context.dataIndex;
              const date = formatDate(dateRange[index]);
              const hour =
                hourlyData[index] && hourlyData[index].hour
                  ? hourlyData[index].hour
                  : null;
              const timeInSeconds =
                hourlyData[index] && hourlyData[index].engagementCount
                  ? hourlyData[index].engagementCount
                  : "0";
              const formattedTime =
                timeInSeconds !== "0" ? formatTime(timeInSeconds) : "0s";
              if (!hour) {
                return `Engagementcount = ${formattedTime}`;
              }
              const startHour24 = parsePeakHourTo24(hour);
              const startLabel = formatPeakHourDisplay(hour);
              const endLabel =
                startHour24 !== null
                  ? formatHour12(startHour24 + 1)
                  : "";
              const rangeLabel = endLabel ? `${startLabel} - ${endLabel}` : startLabel;
              return `Engagementcount = ${formattedTime} between ${rangeLabel} ${tzAbbrev}`;
            },
          },
        },
        datalabels: {
          display: false,
          anchor: "end",
          align: "end",
          color: "red",
          font: {
            weight: "bold",
          },
        },
      },
    };

    return (
      <Grid
        className="card_dashboards"
        style={{
          minHeight: "430px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {globalEngagementLoading ? (
          <DashLoader />
        ) : (
          <>
            <Grid.Row>
              <Grid.Column className="card-header-title">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <div>
                    <Popup
                      content="Identify times of day when users are most active with the bot"
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <label className="chart-title">Peak Engagement Hours</label>
                      }
                    />
                    <h4 className="card-subtitles">Total Chat Time - {chatTime}</h4>
                  </div>
                  <DownloadIcon
                    onExport={handlePeakEngagementExport}
                    filename={`Peak_Engagement_Hours_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`}
                    chartRef={chartRef}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16} className="chart-containers">
                <Line ref={chartRef} data={chartData} options={options} />
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>
    );
  };

  // Most Used Dataset Component
  const renderMostUsedDataset = () => {
    const chartRef = useRef(null);
    const [datasetData, setDatasetData] = useState([]);

    // Process dataset data from global engagement data
    useEffect(() => {
      if (!globalEngagementData) {
        setDatasetData([]);
        return;
      }

      try {
        // Create dataset ID to name map from datasets prop (no API call needed)
        const datasetIdToNameMap = {};
        datasets.forEach((dataset) => {
          datasetIdToNameMap[dataset.datasetId] = dataset.datasetName;
        });

        const mostUsedDatasets = globalEngagementData.mostUsedDatasets || [];

        if (
          mostUsedDatasets.length > 0 &&
          mostUsedDatasets[0].dateRange
        ) {
          const filteredDatasets = mostUsedDatasets[0].datasets.map(
            (dataset) => ({
              datasetName:
                datasetIdToNameMap[dataset.datasetId] || dataset.datasetId,
              count: dataset.count,
              percentageDuration: parseFloat(dataset.totalDuration),
              totalQuestions: dataset.totalQuestions,
            })
          );
          setDatasetData(filteredDatasets);
        } else {
          setDatasetData([]);
        }
      } catch (error) {
        console.error("Error processing dataset data:", error);
        setDatasetData([]);
      }
    }, [globalEngagementData, datasets]);

    const handleMostUsedDatasetExport = async (format, data, filename, chartCanvas) => {
      const exportData = {
        labels: datasetData.map((data) => data.datasetName),
        values: datasetData.map((data) => data.percentageDuration)
      };

      const metadata = {
        title: 'Most Used Dataset',
        subtitle: 'Top 5 most used datasets',
        selectedDataset: selectedDatasetName || 'All Datasets',
        selectedGroup: getGroupName(),
        selectedUsers: getSelectedUsersText(),
        dateRange: `${formatDate(globalStartDate)} - ${formatDate(globalEndDate)}`,
        labelHeader: 'Dataset',
        valueHeader: 'Usage Percentage (%)'
      };

      await handleExport(format, exportData, `Most_Used_Dataset_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`, metadata, chartCanvas);
    };

    const chartData = {
      labels: datasetData.map((data) => data.datasetName),
      datasets: [
        {
          data: datasetData.map((data) => data.percentageDuration),
          borderColor: "#0053ED",
          backgroundColor: "#0053ED",
          borderWidth: 1,
          barPercentage: 0.3,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            callback: function (value) {
              return value + "%";
            },
          },
          grid: {
            display: false,
          },
          title: {
            display: true,
            text: "Dataset  Usage",
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        x: {
          title: {
            display: true,
            text: "Top 5 Most Used Dataset",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const index = context.dataIndex;
              const dataset = datasetData[index];
              return `Total Duration: ${dataset.percentageDuration}%\nTotal Questions: ${dataset.totalQuestions}`;
            },
          },
        },
        datalabels: {
          display: false,
          anchor: "end",
          align: "end",
          color: "red",
          font: {
            weight: "bold",
          },
        },
      },
    };

    return (
      <Grid
        className="card_dashboards"
        style={{
          minHeight: "430px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {globalEngagementLoading ? (
          <DashLoader />
        ) : (
          <>
            <Grid.Row>
              <Grid.Column className="card-header-title">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                  <div>
                    <Popup
                      content="Top 5 most used datasets"
                      inverted
                      position="top left"
                      style={{ borderRadius: "15px" }}
                      trigger={
                        <label className="chart-title">Most Used Dataset</label>
                      }
                    />
                  </div>
                  <DownloadIcon
                    onExport={handleMostUsedDatasetExport}
                    filename={`Most_Used_Dataset_${formatDate(globalStartDate)}_${formatDate(globalEndDate)}`}
                    chartRef={chartRef}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16} className="chart-containers">
                <Bar ref={chartRef} data={chartData} options={options} />
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>
    );
  };

  return (
    <Grid className="dashboards" stackable>
      <Grid.Row columns={2}>
        <Grid.Column className="dashboard-cards">
          <div className="chart-container-engagement">
            {renderAverageEngagementTime()}
          </div>
        </Grid.Column>
        <Grid.Column className="dashboard-cards">
          <div className="chart-container-engagement">
            {renderEngagementTimePerUser()}
          </div>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns={2}>
        <Grid.Column className="dashboard-cards">
          <div className="chart-container-engagement">
            {renderPeakEngagementHours()}
          </div>
        </Grid.Column>
        <Grid.Column className="dashboard-cards">
          <div className="chart-container-engagement">
            {renderMostUsedDataset()}
          </div>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export default EngagementDashboard;
